/**
 * Infer a country from a postal/ZIP code OR a province/state code.
 *
 * Why: customers routinely tab past the Country dropdown (which defaults
 * to United States) and type a Canadian postal code. We can detect this
 * with 99%+ accuracy and auto-correct the Country selection so the
 * downstream Freightcom quote uses the right carrier set.
 *
 * Rules (most specific first):
 *   1. Canadian postal code pattern — `A1A 1A1` / `A1A1A1` (letter-digit-
 *      letter digit-letter-digit, whitespace optional). Unambiguous.
 *   2. Canadian province abbreviation in the 2-char state field (ON, QC,
 *      BC, AB, MB, SK, NS, NB, NL, PE, YT, NT, NU) — also unambiguous.
 *   3. UK postcode pattern — 1–2 letters + digit(s) + optional letter,
 *      space, then 1 digit + 2 letters (e.g., SW1A 1AA, M1 1AE).
 *   4. Mexican postal code pattern — 5 digits starting with 0–9 overlaps
 *      with US ZIP, so we DON'T auto-switch to Mexico purely on postcode.
 *      Only upgrade to Mexico when the state field contains a Mexican
 *      state abbreviation (CDMX, JAL, NLE, etc.) — rare in practice.
 *   5. US ZIP code pattern — 5 digits or 5+4 (`10001-1234`). Default
 *      when no stronger signal exists.
 *
 * Returns `null` when the input is too short/ambiguous to classify
 * (e.g., "123" — could be the start of a CA postal or US ZIP). Callers
 * should only mutate the Country field when a non-null answer is
 * returned, to preserve any manual override the user made.
 */

const CA_POSTAL_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;
const UK_POSTCODE_RE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/;

const CA_PROVINCES = new Set([
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU',
  'ON', 'PE', 'QC', 'SK', 'YT',
]);

// Mexican states (ISO 3166-2:MX subset — no two-letter code collisions
// with Canadian provinces: AGU/BCN/BCS/CAM/CHH/CHP/CMX/COA/COL/DUR/
// GRO/GUA/HID/JAL/MEX/MIC/MOR/NAY/NLE/OAX/PUE/QUE/ROO/SIN/SLP/SON/
// TAB/TAM/TLA/VER/YUC/ZAC). We check a handful of popular ones that
// people actually type — full list too long for practical UI value.
const MX_STATES = new Set([
  'CDMX', 'JAL', 'NLE', 'PUE', 'YUC', 'QROO', 'GTO', 'MOR', 'MEX',
]);

/**
 * @param {string} postalCode - Free-form postal code input, any case/spacing.
 * @param {string} [stateCode] - Optional 2–4 char state/province code.
 * @returns {string|null}  "Canada" | "United States" | "Mexico" | "United Kingdom" | null
 */
export function inferCountry(postalCode, stateCode = '') {
  const pc = (postalCode || '').trim();
  const st = (stateCode || '').trim().toUpperCase();

  if (pc && CA_POSTAL_RE.test(pc)) return 'Canada';
  if (st && CA_PROVINCES.has(st)) return 'Canada';
  if (pc && UK_POSTCODE_RE.test(pc)) return 'United Kingdom';
  if (st && MX_STATES.has(st)) return 'Mexico';
  if (pc && US_ZIP_RE.test(pc)) return 'United States';

  return null;
}

export default inferCountry;
